import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Collapse,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  RestaurantMenu as RestaurantMenuIcon,
  People as PeopleIcon,
  QrCode as QrCodeIcon,
  ShowChart as ShowChartIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
  ExitToApp as LogoutIcon,
  HelpOutline as HelpIcon,
  Description as InvoiceIcon,
  CreditCard as EmoneyIcon,
  Group as CustomerIcon,
  Schedule as ScheduleIcon,
  Rule as RuleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PointOfSale as POSIcon,
  Print as PrintIcon,
  Kitchen as KitchenIcon,
  AccountBalance as AccountingIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const drawerWidth = 240;

const StoreAdminLayout = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [defaultScreenPOS, setDefaultScreenPOS] = useState(false);

  // デフォルト画面設定を読み込み
  useEffect(() => {
    const saved = localStorage.getItem("pos_store_admin_default_screen");
    setDefaultScreenPOS(saved === "pos");
  }, []);

  // デフォルト画面設定を切り替え
  const handleDefaultScreenChange = (e) => {
    const isPOS = e.target.checked;
    setDefaultScreenPOS(isPOS);
    localStorage.setItem("pos_store_admin_default_screen", isPOS ? "pos" : "dashboard");
  };

  // 日常業務メニュー（常時表示）
  const dailyMenu = [
    {
      text: "ダッシュボード",
      icon: <DashboardIcon />,
      path: `/store-admin/${storeId}/dashboard`,
    },
    {
      text: "メニュー管理",
      icon: <RestaurantMenuIcon />,
      path: `/store-admin/${storeId}/menu`,
    },
    {
      text: "スタッフ管理",
      icon: <PeopleIcon />,
      path: `/store-admin/${storeId}/staff`,
    },
    {
      text: "売上管理",
      icon: <ShowChartIcon />,
      path: `/store-admin/${storeId}/sales`,
    },
    {
      text: "顧客管理",
      icon: <CustomerIcon />,
      path: `/store-admin/${storeId}/customers`,
    },
    {
      text: "QRコード",
      icon: <QrCodeIcon />,
      path: `/store-admin/${storeId}/qr`,
    },
  ];

  // 初期設定・詳細設定メニュー（折りたたみ）
  const settingsMenu = [
    {
      text: "店舗設定",
      icon: <SettingsIcon />,
      path: `/store-admin/${storeId}/settings`,
    },
    {
      text: "決済設定",
      icon: <PaymentIcon />,
      path: `/store-admin/${storeId}/payment-settings`,
    },
    {
      text: "電子マネー設定",
      icon: <EmoneyIcon />,
      path: `/store-admin/${storeId}/emoney-settings`,
    },
    {
      text: "税・レシート設定",
      icon: <ReceiptIcon />,
      path: `/store-admin/${storeId}/tax-receipt-settings`,
    },
    {
      text: "インボイス設定",
      icon: <InvoiceIcon />,
      path: `/store-admin/${storeId}/invoice-settings`,
    },
    {
      text: "税率スケジュール",
      icon: <ScheduleIcon />,
      path: `/store-admin/${storeId}/tax-schedules`,
    },
    {
      text: "レシートルール",
      icon: <RuleIcon />,
      path: `/store-admin/${storeId}/receipt-rules`,
    },
    {
      text: "CSV一括登録",
      icon: <CloudUploadIcon />,
      path: `/store-admin/${storeId}/csv-import`,
    },
    {
      text: "プリンター管理",
      icon: <PrintIcon />,
      path: `/store-admin/${storeId}/printers`,
    },
    {
      text: "キッチンプリンター",
      icon: <KitchenIcon />,
      path: `/store-admin/${storeId}/kitchen-printers`,
    },
    {
      text: "レシートカスタマイズ",
      icon: <ImageIcon />,
      path: `/store-admin/${storeId}/receipt-customization`,
    },
    {
      text: "会計ソフト連携",
      icon: <AccountingIcon />,
      path: `/store-admin/${storeId}/accounting`,
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap>
          {user?.storeName || "店舗管理"}
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ pt: 0 }}>
        {/* 日常業務メニュー */}
        {dailyMenu.map((item) => (
          <ListItem disablePadding key={item.text}>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{ py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ my: 1 }} />

        {/* 設定メニュー（折りたたみ） */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setSettingsOpen(!settingsOpen)}
            sx={{ py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="設定"
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
            />
            {settingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {settingsMenu.map((item) => (
              <ListItem disablePadding key={item.text}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                  sx={{ py: 0.5, pl: 4 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>
      <Divider sx={{ mt: 1 }} />

      {/* デフォルト画面設定 */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={defaultScreenPOS}
              onChange={handleDefaultScreenChange}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <POSIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                ログイン時POSを開く
              </Typography>
            </Box>
          }
          sx={{ m: 0 }}
        />
      </Box>

      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate(`/pos/${storeId}`)}
            sx={{ py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <POSIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="POSレジを開く"
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate("/help")}
            sx={{ py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HelpIcon />
            </ListItemIcon>
            <ListItemText
              primary="ヘルプ"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ py: 0.75 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="ログアウト"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            店舗管理システム
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="permanent"
        anchor="left"
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default StoreAdminLayout;
